import React from "react";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleAwardDecoration from "../../../assets/img/circle-award-decoration.png";
import UserFace from "../../../assets/img/avatar-face.jpg";
import { getAwardTheme } from "../goldenAwardTheme";

import "./goldenAwardWinner.css";

const CONFETTI_COLORS = [
  "var(--color-accent-gold)",
  "var(--color-accent-teal)",
  "var(--color-success)",
  "var(--color-bronze)",
  "var(--color-info)",
];
const CONFETTI_PER_CANNON = 20;

// Two cannons planted at the bottom corners fire up and inward, arc, then
// gravity pulls each piece back down past its launch point (clipped by the
// card's overflow: hidden) — a launch-and-fall trajectory instead of a
// plain top-down drift. `side` flips the horizontal sign so one shared
// keyframe (goldenAwardWinner-confettiCannon) works for both cannons.
const makeCannonPieces = (side) =>
  Array.from({ length: CONFETTI_PER_CANNON }, (_, i) => {
    const dir = side === "left" ? 1 : -1; // left cannon fires rightward/inward, right cannon leftward/inward
    const dxPeak = dir * (40 + Math.random() * 170);
    const dyPeak = -(90 + Math.random() * 130);
    return {
      id: `${side}-${i}`,
      side,
      delay: Math.random() * 0.18,
      duration: 1.3 + Math.random() * 0.7,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      rotateStart: Math.random() * 360,
      rotateEnd: Math.random() * 360 + 360,
      dxPeak,
      dyPeak,
      dxEnd: dxPeak + dir * (10 + Math.random() * 40),
      dyEnd: dyPeak + 130 + Math.random() * 110,
      size: 5 + Math.round(Math.random() * 5),
      round: i % 3 === 0,
    };
  });

// One-shot burst, regenerated only when it (re)becomes active — not a
// continuous loop, so it reads as a "splash" rather than a distraction.
const useConfettiPieces = (active) =>
  React.useMemo(() => {
    if (!active) return [];
    return [...makeCannonPieces("left"), ...makeCannonPieces("right")];
  }, [active]);

const GoldenAwardWinner = ({ question, winnerName, winnerPhoto, isCurrentUser = false }) => {
  const isEn = localStorage.language == "en";
  const theme = getAwardTheme(question.question_text);
  const confettiPieces = useConfettiPieces(isCurrentUser);

  return (
    <div
      className={
        "goldenAwardWinner" + (isCurrentUser ? " goldenAwardWinner-self" : "")
      }
    >
      {confettiPieces.length > 0 && (
        <div className="goldenAwardWinner-confetti" aria-hidden="true">
          {confettiPieces.map((p) => (
            <span
              key={p.id}
              className={
                `goldenAwardWinner-confetti-piece goldenAwardWinner-confetti-piece-${p.side}` +
                (p.round ? " goldenAwardWinner-confetti-piece-round" : "")
              }
              style={{
                width: `${p.size}px`,
                height: `${p.size * 1.6}px`,
                background: p.color,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
                "--dxPeak": `${p.dxPeak}px`,
                "--dyPeak": `${p.dyPeak}px`,
                "--dxEnd": `${p.dxEnd}px`,
                "--dyEnd": `${p.dyEnd}px`,
                "--rotateStart": `${p.rotateStart}deg`,
                "--rotateEnd": `${p.rotateEnd}deg`,
              }}
            />
          ))}
        </div>
      )}

      <div className="goldenAwardWinner-top">
        <div className="goldenAwardWinner-badge">
          <img
            src={winnerPhoto || UserFace}
            alt={winnerName}
            className="goldenAwardWinner-photo"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = UserFace;
            }}
          />
          <img src={CircleAwardDecoration} alt="" className="goldenAwardWinner-decoration" />
        </div>
        <div className="goldenAwardWinner-info">
          <span className="goldenAwardWinner-label">
            <EmojiEventsIcon fontSize="inherit" />
            {isCurrentUser
              ? isEn
                ? "YOU ARE THE WINNER"
                : "നിങ്ങളാണ് വിജയി"
              : isEn
                ? "WINNER"
                : "വിജയി"}
          </span>
          <p className="goldenAwardWinner-name">{winnerName}</p>
        </div>
      </div>

      <div className="goldenAwardWinner-divider">
        <span className="goldenAwardWinner-divider-dot" />
      </div>

      {/* Question is intentionally de-emphasized here — the winner is the
          focus of this card, unlike GoldenAwardQuestion's RESULT state. */}
      <div className="goldenAwardWinner-question">
        <div className="goldenAwardWinner-question-badge">
          {theme.image ? (
            theme.tinted ? (
              <span
                className="goldenAwardWinner-question-badgeImg goldenAwardWinner-question-badgeImg-tinted"
                style={{ WebkitMaskImage: `url(${theme.image})`, maskImage: `url(${theme.image})` }}
              />
            ) : (
              <img src={theme.image} alt="" className="goldenAwardWinner-question-badgeImg" />
            )
          ) : (
            <theme.Icon fontSize="inherit" />
          )}
        </div>
        <p className="goldenAwardWinner-question-text">
          {isEn ? question.question_text : question.question_text_malayalam}
        </p>
      </div>

      <div className="goldenAwardWinner-answer">
        <CheckCircleIcon className="goldenAwardWinner-answer-icon" />
        <div>
          <span className="goldenAwardWinner-answer-label">
            {isEn ? "Correct Answer" : "ശരിയായ ഉത്തരം"}
          </span>
          <p className="goldenAwardWinner-answer-value">{question.correct_answer}</p>
        </div>
      </div>
    </div>
  );
};

export default GoldenAwardWinner;
